import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, ExternalLink, TrendingUp } from "lucide-react";
import { CA, PUMP_URL } from "@/data";

const EASE = [0.22, 1, 0.36, 1];
const PAIR = "4q6ozk4rjvxTY4rppV394bxogzRwQu536GTdHiXHc3oq";
const API = `https://api.dexscreener.com/latest/dex/tokens/${CA}`;
const CHART_URL = `https://dexscreener.com/solana/${PAIR}?embed=1&loadChartSettings=0&trades=1&tabs=0&info=0&chartLeftToolbar=0&chartTheme=dark&theme=dark&chartStyle=0&chartType=usd&interval=15`;
const DEX_URL = `https://dexscreener.com/solana/${PAIR}`;

const TABS = [
  { label: "5M", key: "m5" },
  { label: "1H", key: "h1" },
  { label: "6H", key: "h6" },
  { label: "24H", key: "h24" },
];

const fmtPrice = (p) => {
  const n = Number(p);
  return n >= 0.01 ? `$${n.toFixed(4)}` : `$${n.toPrecision(3)}`;
};
const fmtCompact = (n) =>
  n == null ? "—" : `$${Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 2 }).format(n)}`;
const fmtAge = (ms) => {
  if (!ms) return "—";
  const mins = Math.max(1, Math.floor((Date.now() - ms) / 60000));
  if (mins < 60) return `${mins}m old`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h old`;
  return `${Math.floor(hours / 24)}d old`;
};

export default function LiveTerminal() {
  const [pair, setPair] = useState(null);
  const [tab, setTab] = useState("m5");
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(API);
        const json = await res.json();
        const p = json?.pairs?.find((x) => x.pairAddress === PAIR) || json?.pairs?.[0];
        if (alive && p) {
          setPair(p);
          setStale(false);
        }
      } catch {
        if (alive) setStale(true);
      }
    };
    load();
    const id = setInterval(load, 15000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const price = pair ? fmtPrice(pair.priceUsd) : "—";
  const change = pair?.priceChange?.[tab] ?? 0;
  const up = change >= 0;
  const buys = pair?.txns?.[tab]?.buys ?? 0;
  const sells = pair?.txns?.[tab]?.sells ?? 0;
  const total = buys + sells || 1;

  const stats = pair
    ? [
        { label: "Market Cap", value: fmtCompact(pair.marketCap ?? pair.fdv) },
        { label: "24h Volume", value: fmtCompact(pair.volume?.h24) },
        { label: "Liquidity", value: fmtCompact(pair.liquidity?.usd) },
        { label: "Pair Age", value: fmtAge(pair.pairCreatedAt) },
      ]
    : [
        { label: "Market Cap", value: "…" },
        { label: "24h Volume", value: "…" },
        { label: "Liquidity", value: "…" },
        { label: "Pair Age", value: "…" },
      ];

  return (
    <section id="terminal" className="relative bg-[#0A0C10] py-28 sm:py-36" data-testid="terminal-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#E5A93C]">
              04 / Live Terminal
            </p>
            <h2 className="mt-5 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl">
              The <span className="text-[#E5A93C]">$TATE</span> war room.
            </h2>
          </div>
          <span
            className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-[#0E1015] px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-emerald-400"
            data-testid="terminal-live-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Live via Dexscreener
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="mt-14 overflow-hidden rounded-2xl border border-[rgba(229,169,60,0.25)] bg-[#0E1015]"
        >
          {/* Terminal header */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[rgba(229,169,60,0.15)] px-6 py-5">
            <div className="flex flex-wrap items-center gap-4">
              <Activity className="h-5 w-5 text-[#E5A93C]" />
              <span className="font-mono2 text-sm font-bold tracking-wider text-[#F8FAFC]">
                $TATE / SOL
              </span>
              <span className="font-mono2 text-2xl font-bold text-[#F6C85F]" data-testid="terminal-price">
                {price}
              </span>
              <span
                className={`flex items-center gap-1 font-mono2 text-sm font-bold ${
                  up ? "text-emerald-400" : "text-red-400"
                }`}
                data-testid="terminal-change"
              >
                <TrendingUp className={`h-4 w-4 ${up ? "" : "rotate-180"}`} />
                {up ? "+" : ""}
                {change.toFixed(1)}%
              </span>
              {stale && (
                <span className="font-mono2 text-[10px] uppercase tracking-widest text-[#64748B]">
                  reconnecting…
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <div className="flex gap-1.5" data-testid="terminal-timeframes">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`terminal-tf-${t.label.toLowerCase()}`}
                    className={`rounded-lg px-3.5 py-1.5 font-mono2 text-xs font-bold tracking-wider transition-colors duration-300 ${
                      tab === t.key
                        ? "bg-[#E5A93C] text-[#07080A]"
                        : "border border-[rgba(229,169,60,0.25)] text-[#64748B] hover:text-[#F6C85F]"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <a
                href={PUMP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="terminal-buy-button"
                className="ml-1 flex items-center gap-1.5 rounded-lg bg-[#E5A93C] px-3.5 py-1.5 font-mono2 text-xs font-bold tracking-wider text-[#07080A] transition-colors hover:bg-[#F6C85F]"
              >
                Buy <ExternalLink className="h-3 w-3" />
              </a>
              <a
                href={DEX_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="terminal-dex-link"
                className="flex items-center gap-1.5 rounded-lg border border-[rgba(229,169,60,0.25)] px-3.5 py-1.5 font-mono2 text-xs font-bold tracking-wider text-[#64748B] transition-colors hover:text-[#F6C85F]"
              >
                Dexscreener <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_300px]">
            {/* Live chart with trade markers */}
            <div className="h-[340px] sm:h-[420px]" data-testid="terminal-chart">
              <iframe
                src={CHART_URL}
                title="$TATE live chart — Dexscreener"
                className="h-full w-full border-0"
                loading="lazy"
              />
            </div>

            {/* Real buys vs sells */}
            <div className="border-t border-[rgba(229,169,60,0.15)] lg:border-l lg:border-t-0">
              <p className="border-b border-[rgba(229,169,60,0.15)] px-5 py-3.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                Buys vs sells — {TABS.find((t) => t.key === tab).label}
              </p>
              <div className="space-y-6 px-5 py-6">
                <div data-testid="terminal-buys">
                  <div className="flex items-center justify-between">
                    <span className="font-mono2 text-xs font-bold text-emerald-400">BUYS</span>
                    <span className="font-display text-2xl font-extrabold text-[#F8FAFC]">{buys}</span>
                  </div>
                  <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#07080A]">
                    <motion.div
                      animate={{ width: `${(buys / total) * 100}%` }}
                      transition={{ duration: 0.6, ease: EASE }}
                      className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400"
                    />
                  </div>
                  <p className="mt-1.5 font-mono2 text-[10px] text-[#64748B]">
                    {((buys / total) * 100).toFixed(0)}% of flow
                  </p>
                </div>
                <div data-testid="terminal-sells">
                  <div className="flex items-center justify-between">
                    <span className="font-mono2 text-xs font-bold text-red-400">SELLS</span>
                    <span className="font-display text-2xl font-extrabold text-[#F8FAFC]">{sells}</span>
                  </div>
                  <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#07080A]">
                    <motion.div
                      animate={{ width: `${(sells / total) * 100}%` }}
                      transition={{ duration: 0.6, ease: EASE }}
                      className="h-full rounded-full bg-gradient-to-r from-red-600 to-red-400"
                    />
                  </div>
                  <p className="mt-1.5 font-mono2 text-[10px] text-[#64748B]">
                    {((sells / total) * 100).toFixed(0)}% of flow
                  </p>
                </div>
                <div className="rounded-xl border border-[rgba(229,169,60,0.2)] bg-[#07080A] px-4 py-3.5">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                    Volume ({TABS.find((t) => t.key === tab).label})
                  </p>
                  <p className="mt-1 font-display text-xl font-extrabold text-[#F6C85F]" data-testid="terminal-volume">
                    {pair ? fmtCompact(pair.volume?.[tab]) : "…"}
                  </p>
                </div>
                <p className="font-mono2 text-[10px] leading-relaxed tracking-wide text-[#64748B]">
                  Refreshes every 15s. Green/red markers on the chart are real buys and sells.
                </p>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 divide-x divide-[rgba(229,169,60,0.12)] border-t border-[rgba(229,169,60,0.15)] lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="px-6 py-5">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                  {s.label}
                </p>
                <p className="mt-1.5 font-display text-xl font-extrabold text-[#F8FAFC] sm:text-2xl" data-testid={`terminal-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {s.value}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        <p className="mt-5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
          * Live on-chain data via Dexscreener. Not financial advice.
        </p>
      </div>
    </section>
  );
}
